import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { useLocation, useNavigate, matchPath } from "react-router-dom";
import styles from "./App.module.css";

// Import Components
import Header from "./components/Header";
import SocialLinks from "./components/SocialLinks";
import PortfolioList from "./components/PortfolioList";
import ProjectDetail from "./components/ProjectDetail";
import AboutContent from "./components/AboutContent";
import BlogList from "./components/BlogList";
import BlogDetail from "./components/BlogDetail"; // Import the new detail component

// Import Data
import { projects } from "./data/projects";
import { useMobileVibration } from "./lib/vibration";

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  useMobileVibration();

  const [view, setView] = useState("home");
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const [direction, setDirection] = useState("right");
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Store previous view to calculate direction on URL change
  const prevViewRef = useRef("home");

  const viewOrder = ["home", "project", "about", "blog", "blog-post"];

  // URL Listener: Syncs URL path to internal State
  useEffect(() => {
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);

    let nextView = "home";
    let nextProjectId = null;
    let nextPostId = null;
    let nextImageIndex = 0;

    // Route Matching
    const projectMatch = matchPath("/project/:projectId", path);
    const blogMatch = matchPath("/blog/:postId", path);

    if (path === "/about") {
      nextView = "about";
    } else if (path === "/blog") {
      nextView = "blog";
    } else if (projectMatch) {
      nextView = "project";
      nextProjectId = projectMatch.params.projectId;
      const indexParam = searchParams.get("index");
      nextImageIndex = indexParam ? parseInt(indexParam, 10) : 0;
    } else if (blogMatch) {
      nextView = "blog-post";
      nextPostId = blogMatch.params.postId;
    } else {
      nextView = "home";
    }

    // Avoid redundant updates
    if (
      nextView === view &&
      nextProjectId === selectedProjectId &&
      nextPostId === selectedPostId &&
      nextImageIndex === selectedImageIndex
    ) {
      return;
    }

    // Calculate Direction based on previous View (kept in Ref or current State)
    // We use the current 'view' state as the 'from' point.
    let newDirection = "right";
    
    // If we are only changing the image index, we might not want to trigger the page transition animation?
    // But 'isAnimating' usually blocks interactions. 
    // Let's allow direction calculation to proceed.

    if (nextView === "about") {
      newDirection = "down";
    } else if (view === "about") {
      newDirection = "up";
    } else {
      const currentIndex = viewOrder.indexOf(view);
      const nextIndex = viewOrder.indexOf(nextView);
      newDirection = nextIndex > currentIndex ? "right" : "left";
    }

    // Only trigger animation state if the VIEW changes (or major ID changes), 
    // not necessarily for minor query param changes if we don't want to transition the whole page.
    // However, for now, let's keep it consistent with the original logic.
    if (nextView !== view || nextProjectId !== selectedProjectId || nextPostId !== selectedPostId) {
       setDirection(newDirection);
       setIsAnimating(true);
    }

    setView(nextView);
    setSelectedProjectId(nextProjectId);
    setSelectedPostId(nextPostId);
    setSelectedImageIndex(nextImageIndex);
    
    prevViewRef.current = nextView;

  }, [location, view, selectedProjectId, selectedPostId, selectedImageIndex]);

  // Navigation Handlers (UI -> URL)
  const navigateTo = (page) => {
    // If already on the page, do nothing? 
    // The check is now implicit via the URL check in useEffect, 
    // but we can optimize by checking here too if we had the logic available.
    if (isAnimating) return;

    switch (page) {
      case "home":
        navigate("/");
        break;
      case "about":
        navigate("/about");
        break;
      case "blog":
        navigate("/blog");
        break;
      default:
        navigate("/");
    }
  };

  const viewProject = (projectId, imageIndex = 0) => {
    // We push to history. 
    // Note: 'navigateTo' in original code set 'isAnimating' check.
    // We can rely on the button disabling or just let the router handle it.
    if (isAnimating) return;
    navigate(`/project/${projectId}?index=${imageIndex}`);
  };

  const viewPost = (postId) => {
    if (isAnimating) return;
    navigate(`/blog/${postId}`);
  };

  const sidebarVariants = {
    initial: (direction) => {
      if (direction === "down") return { y: "-100%", opacity: 0, x: 0 };
      if (direction === "up") return { y: "100%", opacity: 0, x: 0 };
      return {
        x: direction === "right" ? "100%" : "-100%",
        y: 0,
        opacity: 0,
      };
    },
    animate: (direction) => ({
      x: 0,
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeInOut",
        delay: direction === "up" || direction === "down" ? 0 : 0.2,
      },
    }),
    exit: (direction) => {
      if (direction === "down") return { y: "100%", opacity: 0, x: 0 };
      if (direction === "up") return { y: "-100%", opacity: 0, x: 0 };
      return {
        x: direction === "right" ? "-100%" : "100%",
        y: 0,
        opacity: 0,
        transition: {
          duration: 0.5,
          ease: "easeInOut",
          delay: 0,
        },
      };
    },
  };

  const contentVariants = {
    initial: (direction) => {
      if (direction === "down") return { y: "-100%", opacity: 0, x: 0 };
      if (direction === "up") return { y: "100%", opacity: 0, x: 0 };
      return {
        x: direction === "right" ? "100%" : "-100%",
        y: 0,
        opacity: 0,
      };
    },
    animate: (direction) => ({
      x: 0,
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeInOut",
        delay: 0,
      },
    }),
    exit: (direction) => {
      if (direction === "down") return { y: "100%", opacity: 0, x: 0 };
      if (direction === "up") return { y: "-100%", opacity: 0, x: 0 };
      return {
        x: direction === "right" ? "-100%" : "100%",
        y: 0,
        opacity: 0,
        transition: {
          duration: 0.5,
          ease: "easeInOut",
          delay: direction === "up" || direction === "down" ? 0 : 0.1,
        },
      };
    },
  };

  return (
    <LayoutGroup>
      <Header navigateTo={navigateTo} activeView={view} />

      <div className={styles.mainLayoutWrapper}>
        <aside className={styles.sidebar}>
          <div className={styles.sidebarTopContent}>
            <AnimatePresence mode="wait" custom={direction}>
              {view === "home" && (
                <motion.div
                  key="home-sidebar"
                  custom={direction}
                  variants={sidebarVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <p>self learner, creator, artist</p>
                </motion.div>
              )}
              {view === "project" && selectedProjectId && (
                <motion.div
                  key="proj-desc"
                  custom={direction}
                  variants={sidebarVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <ProjectDetail.Description projectId={selectedProjectId} />
                </motion.div>
              )}
              {view === "blog-post" && selectedPostId && (
                <motion.div
                  key="blog-sidebar"
                  custom={direction}
                  variants={sidebarVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <BlogDetail.Sidebar postId={selectedPostId} />
                </motion.div>
              )}
              {view === "about" && (
                <motion.div
                  key="about-sidebar-placeholder"
                  custom={direction}
                  variants={sidebarVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                />
              )}
              {view === "blog" && (
                <motion.div
                  key="blog-sidebar-placeholder"
                  custom={direction}
                  variants={sidebarVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                />
              )}
            </AnimatePresence>
          </div>

          <AnimatePresence mode="wait" custom={direction}>
            {view === "home" && (
              <motion.div
                key="sidebar-socials"
                custom={direction}
                variants={sidebarVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className={styles.sidebarSocials}
              >
                <SocialLinks />
              </motion.div>
            )}
          </AnimatePresence>
        </aside>

        <main className={styles.mainContent}>
          <AnimatePresence
            mode="wait"
            custom={direction}
            onExitComplete={() => setIsAnimating(false)}
          >
            {view === "home" && (
              <motion.div
                key="portfolio"
                custom={direction}
                variants={contentVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <PortfolioList viewProject={viewProject} />
              </motion.div>
            )}
            {view === "project" && selectedProjectId && (
              <motion.div
                key="project-detail"
                custom={direction}
                variants={contentVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <ProjectDetail.Content
                  projectId={selectedProjectId}
                  activeIndex={selectedImageIndex}
                />
              </motion.div>
            )}
            {view === "about" && (
              <motion.div
                key="about"
                custom={direction}
                variants={contentVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <AboutContent />
              </motion.div>
            )}
            {view === "blog" && (
              <motion.div
                key="blog"
                custom={direction}
                variants={contentVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <BlogList viewPost={viewPost} />
              </motion.div>
            )}
            {view === "blog-post" && selectedPostId && (
              <motion.div
                key="blog-post"
                custom={direction}
                variants={contentVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <BlogDetail.Content postId={selectedPostId} />
              </motion.div>
            )}
          </AnimatePresence>
        </main>

        <footer className={styles.footer}>
          <AnimatePresence>
            {(view === "about" ||
              view === "project" ||
              view === "blog" ||
              view === "blog-post") && (
              <motion.div
                key="footer-socials"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { delay: 0.3 } }}
                exit={{ opacity: 0 }}
                className={styles.footerSocials}
              >
                <SocialLinks />
              </motion.div>
            )}
          </AnimatePresence>
          <p>kado. {new Date().getFullYear()}. all rights deserved</p>
        </footer>
      </div>
    </LayoutGroup>
  );
}

export default App;
